import { Injectable } from "@angular/core";
import { firstValueFrom, retry, timer } from "rxjs";
import { Ehnoteparent } from "../API/ehnoteparent";
import { AuthService } from "./auth-service";

@Injectable({ providedIn: 'root' })
export class AuthBootstrapService {

  // Single in-flight Promise — concurrent callers (APP_INITIALIZER, guards,
  // retry-interceptor refreshes) share the same fetch.
  private inFlight: Promise<void> | null = null;

  constructor(
    private auth: AuthService,
    private api: Ehnoteparent
  ) {}

  /**
   * Resolves once a JWT exists in localStorage. If none, fetches one with
   * retry/backoff. Cold-start safe: a failed first attempt to the auth-token
   * endpoint will be retried up to 3 times (500ms, 1s, 2s) before giving up.
   * Resolves silently even after exhausted retries — guards downstream will
   * catch the resulting 401 via the retry interceptor.
   */
  ensureToken(): Promise<void> {
    if (this.auth.hasToken()) {
      return Promise.resolve();
    }
    if (!this.inFlight) {
      this.inFlight = this.fetchTokenWithRetry().finally(() => {
        this.inFlight = null;
      });
    }
    return this.inFlight;
  }

  /**
   * Force a fresh JWT. Called by the retry-on-401 interceptor when an authed
   * request fails because the stored token is missing/stale/not-yet-synced.
   * Idempotent under concurrent callers — they share the same in-flight fetch.
   */
  refreshToken(): Promise<void> {
    if (this.inFlight) {
      // Another caller is already fetching — ride along instead of clearing
      // their just-fetched token mid-flight.
      return this.inFlight;
    }
    this.auth.clearToken();
    return this.ensureToken();
  }

  private async fetchTokenWithRetry(): Promise<void> {
    try {
      const res: any = await firstValueFrom(
        this.api.GetAuthtokenForAppoinmentPlugin().pipe(
          retry({
            count: 3,
            // Exponential backoff: 500ms, 1s, 2s. Total ceiling ≈ 3.5s of
            // tolerance for a cold-starting backend.
            delay: (_err, attempt) => timer(500 * Math.pow(2, attempt - 1))
          })
        )
      );
      if (res) {
        this.auth.setToken(res);
      }
    } catch (err) {
      // Resolve silently — APP_INITIALIZER must not hang the app boot. The
      // retry interceptor + guards will surface this as /noAccess if the
      // user genuinely can't authenticate.
      console.error('AuthBootstrap: failed to fetch JWT after retries', err);
    }
  }
}
